import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { env } from '@/config/env'

// Lazy load pages for code splitting
const AppShell = lazy(() => import('@/components/layout/AppShell'))
const Login = lazy(() => import('@/pages/Login'))
const Callback = lazy(() => import('@/pages/Callback'))
const Settings = lazy(() => import('@/pages/Settings'))
const History = lazy(() => import('@/pages/History'))
const VoiceSettingsPage = lazy(() => import('@/pages/VoiceSettingsPage'))
const Feeds = lazy(() => import('@/pages/Feeds'))
const ArticlePage = lazy(() => import('@/pages/ArticlePage'))
const SearchResultsPage = lazy(() => import('@/pages/SearchResultsPage'))
const HubPage = lazy(() => import('@/pages/HubPage'))

// Loading fallback
function PageLoader() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <div className="space-y-4 w-64">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  )
}

// Wrap lazy components with Suspense
function withSuspense(Component: React.ComponentType) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  )
}

// Public reader mode (VITE_ENABLE_GENERATOR=false): no auth, no generator routes
const publicRouter = createBrowserRouter([
  {
    path: '/',
    element: withSuspense(AppShell),
    children: [
      {
        index: true,
        element: <Navigate to="/feed" replace />,
      },
      {
        path: 'feed',
        element: null,
      },
      {
        path: 'feeds',
        element: withSuspense(Feeds),
      },
      {
        path: 'article/:id',
        element: withSuspense(ArticlePage),
      },
      {
        path: 'search',
        element: withSuspense(SearchResultsPage),
      },
    ],
  },
  {
    // Hidden hub page — not linked from navigation
    path: '/hub',
    element: withSuspense(HubPage),
  },
  {
    path: '*',
    element: <Navigate to="/feed" replace />,
  },
])

// Full app with generator + auth
const fullRouter = createBrowserRouter([
  {
    path: '/login',
    element: withSuspense(Login),
  },
  {
    path: '/callback',
    element: withSuspense(Callback),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        {withSuspense(AppShell)}
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/feed" replace />,
      },
      {
        path: 'feed',
        element: null,
      },
      {
        path: 'chat',
        element: null,
      },
      {
        path: 'article/:id',
        element: withSuspense(ArticlePage),
      },
      {
        path: 'search',
        element: withSuspense(SearchResultsPage),
      },
      {
        path: 'settings',
        element: withSuspense(Settings),
      },
      {
        path: 'settings/voice',
        element: withSuspense(VoiceSettingsPage),
      },
      {
        path: 'history',
        element: withSuspense(History),
      },
      {
        path: 'feeds',
        element: withSuspense(Feeds),
      },
    ],
  },
  {
    // Hidden hub page — not linked from navigation
    path: '/hub',
    element: withSuspense(HubPage),
  },
  {
    path: '*',
    element: <Navigate to="/feed" replace />,
  },
])

export const router = env.generatorEnabled ? fullRouter : publicRouter
